import * as Sentry from "@sentry/capacitor";
import describeNetworkError from "../lib/describeNetworkError";
import logger from "../lib/sentryLogger";
import React, {useState, createContext} from "react";
import {logManagerAction} from "../lib/api/logs";
import {logTillAssignment} from "../lib/api/till";
import {openCashDrawer} from "../lib/CashDrawer/CashDrawerPlugin";
import {useAppSelector} from "../lib/hooks";

const strings = {
    cashDrawerError:
        "An unexpected error occurred when establishing a connection with the cash drawer.",
    cashDrawerErrorWithMessage: (errMessage: string) =>
        `There was an error related to the cash drawer: ${errMessage}`,
    skipAssignmentErrored: "Failed to skip till assignment. Please try again.",
};

type TillContext = {
    assignedTill: number;
    setAssignedTill: (
        tillNumber: number,
        modifyLocalStateOnly?: boolean,
    ) => Promise<void>;
    tillAssignmentSkipped: boolean; // can only be true when POS is used as drive_thru_order_taker
    setTillAssignmentSkipped: (newAssignmentSkipped: boolean) => void;
    onSelectNoTillOverride: (managerId: string) => Promise<void>;
    trainingModeAssignedTill: number;
    setTrainingModeAssignedTill: (tillNumber: number) => void;
    assigningTill: boolean;
    errorMessage: string | undefined;
    clearErrorMessage: () => void;
};

export const NO_TILL = -1;

export const TillAssignmentContext = createContext<TillContext>({
    assignedTill: NO_TILL,
    setAssignedTill: () => Promise.resolve(),
    tillAssignmentSkipped: false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setTillAssignmentSkipped: () => {},
    onSelectNoTillOverride: () => Promise.resolve(),
    trainingModeAssignedTill: NO_TILL,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setTrainingModeAssignedTill: () => {},
    assigningTill: false,
    errorMessage: undefined,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    clearErrorMessage: () => {},
});

export function TillAssignmentProvider(props: {children: React.ReactNode}) {
    const [assignedTill, setAssignedTill] = useState(NO_TILL);
    const [tillAssignmentSkipped, setTillAssignmentSkipped] = useState(false);
    const [trainingModeAssignedTill, setTrainingModeAssignedTill] =
        useState(NO_TILL);
    const [assigningTill, setAssigningTill] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
        undefined,
    );
    const deviceId = useAppSelector((state) => state.pos.deviceId);
    const stationMode = useAppSelector((state) => state.pos.station.mode);

    const onAssignTill = async (
        tillNumber: number,
        modifyLocalStateOnly = false,
    ) => {
        if (modifyLocalStateOnly) {
            setAssignedTill(tillNumber);
            return Promise.resolve();
        }

        setAssigningTill(true);
        // if we can't open the till that is connected, we shouldn't get to a state which makes it seem like we have a successful assignment.
        return openCashDrawer()
            .then(() => {
                return logTillAssignment(deviceId ?? "", tillNumber);
            })
            .then(() => {
                setAssignedTill(tillNumber);
            })
            .catch((err) => {
                let errMsg = "";
                if (!err.config) {
                    // not an axios error, cash drawer error
                    if (err.message) {
                        errMsg = strings.cashDrawerErrorWithMessage(
                            err.message,
                        );
                    } else {
                        errMsg = strings.cashDrawerError;
                    }
                    Sentry.captureMessage(errMsg);
                } else {
                    errMsg = describeNetworkError(err).join("\n");
                    logger.warn("Failed to log till assignment.", {
                        errorMessage: errMsg,
                    });
                }
                setErrorMessage(errMsg);
            })
            .finally(() => {
                setAssigningTill(false);
            });
    };

    const onSelectNoTillOverride = (managerId: string) => {
        return Promise.allSettled([
            logManagerAction("no-till-override", managerId, stationMode),
            logTillAssignment(deviceId ?? "", NO_TILL),
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ]).then(([_, logTillAssignmentRes]) => {
            if (logTillAssignmentRes.status === "fulfilled") {
                setTillAssignmentSkipped(true);
            } else {
                logger.warn("Failed to skip till assignment.");
                setErrorMessage(strings.skipAssignmentErrored);
            }
        });
    };

    const clearErrorMessage = () => {
        setErrorMessage(undefined);
    };

    return (
        <TillAssignmentContext.Provider
            value={{
                assignedTill: assignedTill,
                setAssignedTill: onAssignTill,
                tillAssignmentSkipped: tillAssignmentSkipped,
                setTillAssignmentSkipped: setTillAssignmentSkipped,
                onSelectNoTillOverride: onSelectNoTillOverride,
                trainingModeAssignedTill: trainingModeAssignedTill,
                setTrainingModeAssignedTill: setTrainingModeAssignedTill,
                assigningTill: assigningTill,
                errorMessage: errorMessage,
                clearErrorMessage: clearErrorMessage,
            }}>
            {props.children}
        </TillAssignmentContext.Provider>
    );
}
